import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IconReusable from '../../../../components/IconReusable';
import { CustomButton } from '../../../MUI_custom_component';
import { downloadNpsTransactionStatementAction } from '../APIs/action';
import StatusBadge from '../../../MUI_custom_component/Badge/StatusBadge';

const metricCardStyle = {
  border: '1px solid rgba(255,255,255,0.8)',
  borderRadius: 1,
  p: '9px',
};

const NPSOverviewSection = ({
  npsOverview,
  company_esi_id,
  manufacturer_id,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!npsOverview) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadNpsTransactionStatementAction(
        company_esi_id,
        manufacturer_id,
        'NPS Transaction Statement.pdf',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const {
    header_title,
    pran_info,
    tier_info,
    scheme_info,
    invested_amount_info,
    amount_withdrawal_info,
    xirr_info,
    current_amount_info,
    is_sot_visible,
  } = npsOverview;
  console.log(npsOverview, 'npsOvierview');

  const isGain = current_amount_info?.is_gain;

  return (
    <Box
      sx={{
        background: '#0081F2',
        borderRadius: 2,
        p: 2,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Decorative blur overlay */}
      <Box
        sx={{
          position: 'absolute',
          width: 236,
          height: 226,
          top: -97,
          right: -99,
          borderRadius: '50%',
          background: 'rgba(177,211,219,0.14)',
          filter: 'blur(32px)',
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      <Typography
        variant='h3'
        sx={{ color: '#fff', mb: 0.5 }}
      >
        {header_title}
      </Typography>

      {/* PRAN Row */}
      {pran_info && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography
            variant='labelD'
            sx={{ color: '#fff' }}
          >
            {pran_info.display_name}
          </Typography>
          <Typography
            variant='body1'
            sx={{ color: '#fff' }}
          >
            {pran_info.value}
          </Typography>
        </Box>
      )}

      {/* Tier + Scheme */}
      {(tier_info || scheme_info) && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {tier_info && (
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='labelD'
                sx={{ color: '#fff', display: 'block' }}
              >
                {tier_info.display_name}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#fff' }}
              >
                {tier_info.value}
              </Typography>
            </Box>
          )}
          {scheme_info && (
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='labelD'
                sx={{ color: '#fff', display: 'block' }}
              >
                {scheme_info.display_name}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#fff' }}
              >
                {scheme_info.value}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Metric Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Invested Amount */}
        {invested_amount_info && (
          <Box sx={metricCardStyle}>
            <Typography
              variant='labelD'
              sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}
            >
              {invested_amount_info.display_name}
            </Typography>
            <Typography
              variant='h2'
              sx={{ color: '#fff' }}
            >
              {invested_amount_info.value}
            </Typography>
          </Box>
        )}

        {/* Amount Withdrawn + XIRR */}
        {(amount_withdrawal_info || xirr_info) && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {amount_withdrawal_info && (
              <Box sx={{ ...metricCardStyle, flex: 1 }}>
                <Typography
                  variant='labelD'
                  sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}
                >
                  {amount_withdrawal_info.display_name}
                </Typography>
                <Typography
                  variant='h2'
                  sx={{ color: '#fff' }}
                >
                  {amount_withdrawal_info.value}
                </Typography>
              </Box>
            )}
            {xirr_info && (
              <Box sx={{ ...metricCardStyle, flex: 1 }}>
                <Typography
                  variant='labelD'
                  sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}
                >
                  {xirr_info.display_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant='h2'
                    sx={{ color: '#fff' }}
                  >
                    {xirr_info.value}
                  </Typography>
                  <IconReusable
                    name='TrendingUp'
                    color='#fff'
                    width={12}
                    height={7}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Current Amount */}
        {current_amount_info && (
          <Box
            sx={{
              background: '#fff',
              borderRadius: 1,
              p: 2,
              boxShadow: '0px 1px 1px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              variant='h2'
              sx={{ color: '#666', display: 'block' }}
            >
              {current_amount_info.display_name}
            </Typography>
            <Typography
              variant='title'
              sx={{ color: 'primary.main', display: 'block' }}
            >
              {current_amount_info.value}
            </Typography>
            {(current_amount_info.amount || current_amount_info.message) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {current_amount_info.amount && (
                  <StatusBadge
                    label={current_amount_info.amount}
                    color={isGain ? '#008000' : '#CC0000'}
                    size='medium'
                  />
                )}
                {current_amount_info.message && (
                  <Typography
                    variant='body1'
                    sx={{ color: '#666' }}
                  >
                    {current_amount_info.message}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      {is_sot_visible && (
        <CustomButton
          variant='filled'
          color='primary'
          label='NPS Transaction Statement'
          leftIcon={FileDownloadOutlinedIcon}
          loading={isDownloading}
          onClick={handleDownload}
          fullWidth
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default NPSOverviewSection;
